import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, ShieldCheck, FileText, Activity, ShoppingBag, Download, CheckCircle, XCircle, Loader2, Globe, Pencil, Bell, ChevronDown } from 'lucide-react';
import { useState, useContext, useEffect } from 'react';
import { FounderContext } from '@/App';
import PsychosocialSession from './PsychosocialSession';
import PatientCaregiverResources from './PatientCaregiverResources';
import PhysicianOutput from './PhysicianOutput';
import PatientResultCard from './PatientResultCard';
import { CATALOG_ENGINE_COUNT } from '@/lib/engineCount';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TRANSLATE_LANGS = {
  'hi-IN': 'Hindi', 'ta-IN': 'Tamil', 'te-IN': 'Telugu', 'kn-IN': 'Kannada',
  'ml-IN': 'Malayalam', 'bn-IN': 'Bengali', 'mr-IN': 'Marathi', 'gu-IN': 'Gujarati',
  'pa-IN': 'Punjabi', 'od-IN': 'Odia', 'as-IN': 'Assamese', 'ur-IN': 'Urdu',
  'ne-IN': 'Nepali', 'kok-IN': 'Konkani', 'ks-IN': 'Kashmiri', 'sd-IN': 'Sindhi',
  'sa-IN': 'Sanskrit', 'sat-IN': 'Santali', 'mni-IN': 'Manipuri', 'brx-IN': 'Bodo',
  'mai-IN': 'Maithili', 'doi-IN': 'Dogri',
};

export function downloadPDF(url) {
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = url;
  document.body.appendChild(iframe);
  setTimeout(() => document.body.removeChild(iframe), 30000);
}

export function NudgeInbox({ encounterId }) {
  const [inbox, setInbox] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!encounterId) return;
    let alive = true;
    fetch(`${API}/nudges/inbox/${encounterId}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (alive) setInbox(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [encounterId]);

  async function act(nudgeId, action) {
    setBusy(nudgeId + ':' + action);
    try {
      const r = await fetch(`${API}/nudges/${nudgeId}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      if (r.ok) {
        const refreshed = await fetch(`${API}/nudges/inbox/${encounterId}`).then(rr => rr.json());
        setInbox(refreshed);
      }
    } catch (err) {
      // Nudge inbox refresh is best-effort — log for debugging but do not block UI
      console.warn('[ClinicalOutput] Nudge inbox refresh failed:', err?.message || err);
    }
    setBusy(null);
  }

  if (!inbox || !inbox.nudges || inbox.nudges.length === 0) return null;

  const levelColor = {
    primordial: 'bg-slate-100 text-slate-800',
    primary:    'bg-emerald-100 text-emerald-800',
    secondary:  'bg-amber-100 text-amber-800',
    tertiary:   'bg-orange-100 text-orange-800',
  };
  const statusColor = {
    scheduled: 'text-indigo-700',
    completed: 'text-emerald-700 line-through',
    snoozed:   'text-slate-500',
    dismissed: 'text-slate-400 line-through',
  };

  return (
    <div className="mt-4 bg-white border-2 border-indigo-200 rounded-xl p-4" data-testid="nudge-inbox">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-indigo-700" />
          <h4 className="font-bold text-indigo-900 text-sm">24/7 Preventive Care Reminders</h4>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-mono">Engine 28</span>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wide text-indigo-600">Projected avoided cost</div>
          <div className="text-base font-bold text-indigo-900" data-testid="nudge-inbox-avoided-cost">
            ${(inbox.total_avoided_cost_usd || 0).toLocaleString()}
          </div>
        </div>
      </div>
      <p className="text-[11px] italic text-slate-600 mb-3">
        Deterministic preventive nudges scheduled from your matched evidence-graded rules. No AI invention — every reminder traces to a sealed rule + source. You decide what to act on.
      </p>
      <div className="space-y-2">
        {inbox.nudges.map((n) => (
          <div
            key={n.nudge_id}
            className="flex items-start justify-between gap-3 border border-slate-200 rounded-lg p-2.5 text-sm"
            data-testid={`nudge-row-${n.rule_id}-d${n.cadence_day_offset}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${levelColor[n.prevention_level] || 'bg-slate-100'}`}>
                  {n.prevention_level}
                </span>
                <span className="font-semibold text-slate-900 truncate">{n.rule_name}</span>
                <span className="text-[10px] text-slate-400">in {n.cadence_day_offset}d</span>
              </div>
              <div className={`text-slate-700 ${statusColor[n.status] || ''}`}>{n.message}</div>
              <div className="text-[10px] text-slate-500 italic mt-0.5">
                {n.source_code} {n.evidence_grade ? `· Grade ${n.evidence_grade}` : ''} {n.icd10_z_code ? `· ${n.icd10_z_code}` : ''}
              </div>
            </div>
            {n.status === 'scheduled' && (
              <div className="flex flex-col gap-1 shrink-0">
                <button
                  onClick={() => act(n.nudge_id, 'done')}
                  disabled={busy === n.nudge_id + ':done'}
                  className="text-[10px] px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                  data-testid={`nudge-action-done-${n.rule_id}-d${n.cadence_day_offset}`}
                >Done</button>
                <button
                  onClick={() => act(n.nudge_id, 'snooze_30d')}
                  disabled={busy === n.nudge_id + ':snooze_30d'}
                  className="text-[10px] px-2 py-1 rounded bg-slate-200 text-slate-800 hover:bg-slate-300 disabled:opacity-50"
                  data-testid={`nudge-action-snooze-${n.rule_id}-d${n.cadence_day_offset}`}
                >Snooze 30d</button>
                <button
                  onClick={() => act(n.nudge_id, 'not_relevant')}
                  disabled={busy === n.nudge_id + ':not_relevant'}
                  className="text-[10px] px-2 py-1 rounded bg-white text-slate-600 border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
                  data-testid={`nudge-action-dismiss-${n.rule_id}-d${n.cadence_day_offset}`}
                >Not relevant</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Evidence Map (June 2026) ──────────────────────────────────────────────
// Per-encounter badges of the validated, deterministic instruments that fired,
// each with its source guideline (e.g., "NEWS2 = 13 · RCP"). Pure presentation
// derived from riskFlags already in the response — NO LLM, additive, no backend
// change. Every score traces to a sealed rule + published source.
const SOURCE_ABBR = [
  [/royal college of physicians/i, 'RCP'],
  [/acc\/aha|aha\/acc|american college of cardiology|american heart association/i, 'ACC/AHA'],
  [/world health organization|\bwho\b/i, 'WHO'],
  [/national comprehensive cancer network|\bnccn\b/i, 'NCCN'],
  [/american diabetes association|\bada\b/i, 'ADA'],
  [/ats\/idsa/i, 'ATS/IDSA'],
  [/\bidsa\b|infectious diseases society/i, 'IDSA'],
  [/\beular\b/i, 'EULAR'],
  [/american college of rheumatology|\bacr\b/i, 'ACR'],
  [/\bkdigo\b/i, 'KDIGO'],
  [/\baasld\b/i, 'AASLD'],
  [/\bgina\b/i, 'GINA'],
  [/global initiative for chronic obstructive|\bgold\b/i, 'GOLD'],
  [/american society of hematology|\bash\b/i, 'ASH'],
  [/\bisth\b/i, 'ISTH'],
  [/centers for disease control|\bcdc\b|\bacip\b/i, 'CDC/ACIP'],
  [/\bnice\b/i, 'NICE'],
  [/national psoriasis foundation|\baad\b/i, 'AAD'],
];

function emShortSource(flag) {
  const cites = flag.evidence_citations || [];
  const c = (cites.length ? cites[0] : '') || flag.source_guideline || flag.sourceGuideline || '';
  if (!c) return '';
  const yr = (String(c).match(/\b(19|20)\d{2}\b/) || [''])[0];
  for (const [re, ab] of SOURCE_ABBR) {
    if (re.test(c)) return yr ? `${ab} ${yr}` : ab;
  }
  let first = String(c).split(/[;.,]/)[0].trim();
  const surname = (first.match(/^([A-Z][a-zA-Z'-]+)/) || ['', ''])[1];
  if (surname && yr) return `${surname} ${yr}`;
  if (first.length > 24) first = first.slice(0, 22).trim() + '…';
  return yr && !first.includes(yr) ? `${first} ${yr}` : first;
}

function emScoredInstrument(category) {
  if (!category) return null;
  // Primary: "INSTRUMENT = value" / "INSTRUMENT ≈ value" (value N, N.N or N/M).
  const m = category.match(/([=≈])\s*(\d+(?:\.\d+)?(?:\s*\/\s*\d+)?)/);
  if (m) {
    const op = m[1];
    const val = m[2].replace(/\s+/g, '');
    let instr = category.slice(0, m.index).split('(').pop().split('/').pop();
    instr = instr.replace(/[—,:\-]\s*$/, '').trim();
    if (!instr || instr.length > 30) return null;
    return `${instr} ${op} ${val}`;
  }
  // Secondary: named scales without "=".
  const nyha = category.match(/NYHA\s+(?:CLASS\s+)?([IVX]+)/i);
  if (nyha) return `NYHA ${nyha[1].toUpperCase()}`;
  const cp = category.match(/CHILD-?PUGH[^A-Za-z]*?CLASS\s+([A-C])/i);
  if (cp) return `Child-Pugh ${cp[1].toUpperCase()}`;
  const gold = category.match(/GOLD\s+(?:group\s+)?([A-E1-4])/i);
  if (gold) return `GOLD ${gold[1].toUpperCase()}`;
  const bsa = category.match(/BSA\s+(\d{1,3})\s*%/i);
  if (bsa) return `BSA ${bsa[1]}%`;
  return null;
}

const EM_SEV_STYLE = {
  CRITICAL: 'bg-red-50 border-red-300 text-red-800',
  HIGH: 'bg-orange-50 border-orange-300 text-orange-800',
  MODERATE: 'bg-amber-50 border-amber-300 text-amber-800',
  LOW: 'bg-emerald-50 border-emerald-300 text-emerald-800',
};

const EM_SEV_INTERP = {
  CRITICAL: 'critical — immediate emergency response',
  HIGH: 'high risk — urgent clinical review',
  MODERATE: 'moderate — clinical correlation advised',
  LOW: 'low risk — routine follow-up',
};

// Descriptive band from the category tail (text after the first em-dash), e.g.
// "… — SEVERE (≥3) — assess for ICU" → "SEVERE (≥3) — assess for ICU".
function emBand(category) {
  if (!category) return '';
  const i = category.indexOf('—');
  if (i === -1) return '';
  let band = category.slice(i + 1).replace(/\s+/g, ' ').trim();
  if (band.length > 80) band = band.slice(0, 78).trim() + '…';
  return band;
}

// Full primary citation(s) for the expanded view (not the abbreviated pill source).
function emFullCitation(flag) {
  const cites = flag.evidence_citations || [];
  if (cites.length) return cites.join('; ');
  return flag.source_guideline || flag.sourceGuideline || '';
}

// Clinical domain = the category prefix before the first "/" (e.g. "GI BLEED / AIMS65 = 4 — …" → "GI BLEED").
function emDomain(category) {
  if (!category) return 'GENERAL';
  const i = category.indexOf('/');
  if (i === -1) return 'GENERAL';
  const dom = category.slice(0, i).replace(/\s+/g, ' ').trim();
  return dom || 'GENERAL';
}

function emSlug(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

const EM_SEV_RANK = { CRITICAL: 0, HIGH: 1, MODERATE: 2, LOW: 3 };

// Size of the deterministic instrument library (307 = 218 legacy + 88 + 1 meta).
// Display-only (rendered in the evidence-map subtitle); NOT used in any calculation.
// Single source of truth: @/lib/engineCount (mirrors backend CATALOG_ENGINE_COUNT).
const INSTRUMENT_LIBRARY_COUNT = CATALOG_ENGINE_COUNT;


export function EvidenceMapBadges({ riskFlags, evidenceMap }) {
  const [openIdx, setOpenIdx] = useState(null);
  const badges = [];
  const seen = new Set();
  // Prefer the full PRE-priority-filter Evidence Map (every scored instrument that
  // fired this encounter); fall back to post-filter riskFlags for older encounters
  // that predate the field.
  const source = (Array.isArray(evidenceMap) && evidenceMap.length > 0) ? evidenceMap : (riskFlags || []);
  source.forEach((flag) => {
    const instr = emScoredInstrument(flag.category);
    if (!instr) return;
    const src = emShortSource(flag);
    const key = `${instr}|${src}`;
    if (seen.has(key)) return;
    seen.add(key);
    const severity = (flag.severity || 'MODERATE').toUpperCase();
    badges.push({
      idx: badges.length,
      instr,
      src,
      severity,
      interp: EM_SEV_INTERP[severity] || EM_SEV_INTERP.MODERATE,
      band: emBand(flag.category),
      citation: emFullCitation(flag),
      domain: emDomain(flag.category),
    });
  });
  if (badges.length === 0) return null;

  // Group the scored instruments by clinical domain, ordered by the most acute
  // finding inside each domain (CRITICAL → LOW), then alphabetically.
  const groupMap = new Map();
  badges.forEach((b) => {
    if (!groupMap.has(b.domain)) groupMap.set(b.domain, []);
    groupMap.get(b.domain).push(b);
  });
  const groups = Array.from(groupMap.entries()).map(([domain, items]) => ({
    domain,
    items,
    topRank: Math.min(...items.map((x) => EM_SEV_RANK[x.severity] ?? 2)),
  }));
  groups.sort((a, b) => (a.topRank - b.topRank) || a.domain.localeCompare(b.domain));

  const open = openIdx !== null ? badges[openIdx] : null;
  const nInstr = badges.length;
  const nDomains = groups.length;

  return (
    <div data-testid="evidence-map-badges" className="border-2 border-slate-200 rounded-xl p-3 bg-slate-50/60">
      <div
        data-testid="evidence-map-headline"
        className="flex items-center gap-1.5 mb-1 text-[13px] font-bold text-slate-800"
      >
        <ShieldCheck className="w-4 h-4 text-[#0369A1] flex-shrink-0" />
        <span>
          <span data-testid="evidence-map-instrument-count" className="text-[#0369A1]">{nInstr}</span>{' '}
          validated instrument{nInstr === 1 ? '' : 's'} evaluated this encounter
        </span>
      </div>
      <div className="text-[11px] text-slate-500 mb-2.5">
        across {nDomains} clinical domain{nDomains === 1 ? '' : 's'} · of {INSTRUMENT_LIBRARY_COUNT} deterministic instruments screened · tap a chip for details
      </div>
      <div className="space-y-2.5">
        {groups.map((g) => (
          <div key={g.domain} data-testid={`evidence-map-domain-${emSlug(g.domain)}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1 flex items-center gap-1.5">
              <span>{g.domain}</span>
              <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-slate-200 text-slate-600 text-[9px] font-bold">
                {g.items.length}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {g.items.map((b) => (
                <button
                  key={`emb-${b.idx}`}
                  type="button"
                  data-testid={`evidence-badge-${b.idx}`}
                  aria-expanded={openIdx === b.idx}
                  onClick={() => setOpenIdx(openIdx === b.idx ? null : b.idx)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[12px] font-medium cursor-pointer transition-shadow hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-300 ${EM_SEV_STYLE[b.severity] || EM_SEV_STYLE.MODERATE}`}
                >
                  <span className="font-semibold">{b.instr}</span>
                  {b.src && <span className="opacity-70">· {b.src}</span>}
                  <ChevronDown className={`w-3 h-3 opacity-60 transition-transform ${openIdx === b.idx ? 'rotate-180' : ''}`} />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {open && (
        <div
          data-testid={`evidence-badge-detail-${openIdx}`}
          className="mt-2.5 p-2.5 rounded-lg bg-white border border-slate-200 text-[12px] animate-in fade-in"
        >
          <p className="text-slate-800">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mr-1.5">{open.domain}</span>
            <span className="font-semibold">{open.instr}</span>
            <span className="text-slate-500"> → </span>
            <span>{open.interp}</span>
            {open.band && <span className="text-slate-500"> · {open.band}</span>}
          </p>
          {open.citation && (
            <p className="text-slate-500 italic mt-1" data-testid={`evidence-badge-citation-${openIdx}`}>
              Source: {open.citation}
            </p>
          )}
        </div>
      )}
      <p className="text-[10px] italic text-slate-400 mt-2.5">Deterministic — every score traces to a sealed rule + published source. No AI invention.</p>
    </div>
  );
}


export function RiskFlagCard({ flag }) {
  const cls = flag.severity === 'CRITICAL' ? 'risk-critical' : flag.severity === 'HIGH' ? 'risk-high' : 'risk-moderate';
  const [showProv, setShowProv] = useState(false);
  // Moat 1 — prefer the new decorator fields; fall back to legacy ones.
  const sourceEngine = flag.source_engine || flag.engine || flag.engine_id;
  const sourceFile = flag.source_file || null;
  const evidenceCitations = flag.evidence_citations || [];
  const legalBasis = flag.legal_basis || null;
  const ruleId = flag.rule_id || flag.ruleId || sourceEngine;
  const sourceGuideline = flag.source_guideline || flag.sourceGuideline || (evidenceCitations[0] || (ruleId ? 'guideline_pending' : null));
  return (
    <div className={`p-4 rounded-xl mb-2 ${cls}`} data-testid={`risk-flag-${flag.severity.toLowerCase()}`}>
      <div className="flex items-start gap-2">
        {flag.severity === 'CRITICAL' ? (
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-600 flex items-center justify-center animate-pulse" data-testid="red-plus-icon">
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="12" y1="6" x2="12" y2="18"/><line x1="6" y1="12" x2="18" y2="12"/></svg>
          </div>
        ) : (
          <AlertTriangle className="w-4 h-4 mt-0.5" />
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge className={`text-[10px] ${flag.severity === 'CRITICAL' ? 'bg-red-600' : flag.severity === 'HIGH' ? 'bg-orange-500' : 'bg-amber-500'} text-white`}>
              {flag.severity}
            </Badge>
            <span className="font-semibold text-sm">{flag.category}</span>
            {flag.snomed?.code && (
              <span
                className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-indigo-700 text-white"
                data-testid={`flag-snomed-${flag.snomed.code}`}
                title={`${flag.snomed.name} — UMLS-mapped via NLM`}
              >
                SNOMED {flag.snomed.code}
              </span>
            )}
          </div>
          <p className="text-sm">{flag.message}</p>
          {flag.triggers?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {flag.triggers.map((t, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white/50">{t}</span>)}
            </div>
          )}
          {/* Cures Act provenance — physician-facing audit trail per flag */}
          {(ruleId || sourceGuideline) && (
            <div className="mt-2" data-testid={`flag-provenance-${ruleId || 'unknown'}`}>
              <button
                type="button"
                onClick={() => setShowProv(s => !s)}
                className="text-[10px] uppercase tracking-wide text-slate-500 hover:text-slate-700 underline"
                data-testid={`flag-prov-toggle-${ruleId || 'unknown'}`}
              >
                {showProv ? 'Hide source & evidence' : 'Why this flag fired'}
              </button>
              {showProv && (
                <div className="mt-1 bg-white/80 border border-slate-300 rounded p-2.5 text-[11px] leading-snug space-y-1" data-testid={`flag-source-tooltip-${ruleId || 'unknown'}`}>
                  <div><span className="font-mono text-slate-500">source_engine:</span> {sourceEngine || '(unknown)'}</div>
                  {sourceFile && <div><span className="font-mono text-slate-500">source_file:</span> <code className="bg-slate-100 px-1">{sourceFile}</code></div>}
                  {evidenceCitations.length > 0 && (
                    <div>
                      <span className="font-mono text-slate-500">evidence_citations:</span>
                      <ul className="ml-3 mt-0.5 list-disc">
                        {evidenceCitations.map((c, i) => <li key={i} className="text-slate-700">{c}</li>)}
                      </ul>
                    </div>
                  )}
                  {flag.triggers?.length > 0 && (
                    <div><span className="font-mono text-slate-500">trigger_tokens:</span> {flag.triggers.join(' | ')}</div>
                  )}
                  {legalBasis && (
                    <div className="pt-1 mt-1 border-t border-slate-200 text-[10px] italic text-slate-600">{legalBasis}</div>
                  )}
                  {flag.llm_in_decision === false && (
                    <div className="text-[10px] font-bold text-emerald-700">✓ NO LLM in this decision — deterministic rule output</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// OPQRST capture-completeness chip — reads the deterministic OLDCARTS/OPQRST
// extraction (result.chiefComplaintStructured). Signals to clinicians/validators
// that intake is structured, not free-text. No LLM.
export function OpqrstChip({ cc }) {
  if (!cc) return null;
  const has = (v) => (Array.isArray(v) ? v.length > 0 : !!v);
  const items = [
    ['O', 'Onset', has(cc.onset && (cc.onset.type || cc.onset.when))],
    ['P', 'Provocation / Palliation', has((cc.aggravators || []).length || (cc.alleviators || []).length)],
    ['Q', 'Quality', has(cc.character)],
    ['R', 'Radiation', has(cc.radiation)],
    ['S', 'Severity', has(cc.severity && ((cc.severity.score !== null && cc.severity.score !== undefined) || cc.severity.descriptor))],
    ['T', 'Timing', has(cc.duration && (cc.duration.pattern || cc.duration.value))],
  ];
  const n = items.filter((i) => i[2]).length;
  return (
    <div
      data-testid="opqrst-completeness-chip"
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white text-[12px] font-semibold text-slate-700 mb-3"
    >
      <span
        className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[11px] font-bold ${n === 6 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
      >
        {n}
      </span>
      <span className="text-slate-500 font-normal">/6 OPQRST captured</span>
      <span className="flex items-center gap-1">
        {items.map(([letter, title, ok]) => (
          <span
            key={letter}
            title={`${title}: ${ok ? 'captured' : 'not captured'}`}
            data-testid={`opqrst-${letter.toLowerCase()}-${ok ? 'on' : 'off'}`}
            className={`inline-flex items-center justify-center w-4 h-4 rounded text-[10px] font-bold ${ok ? 'bg-[#0369A1] text-white' : 'bg-slate-200 text-slate-400'}`}
          >
            {letter}
          </span>
        ))}
      </span>
    </div>
  );
}


export default function ClinicalOutput({ result, viewMode = 'physician', onEdit, onViewPatient }) {
  const [translateLang, setTranslateLang] = useState('hi-IN');
  const [translatedSoap, setTranslatedSoap] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translateError, setTranslateError] = useState(null);
  const [physicianView, setPhysicianView] = useState('full');
  const [showAttestation, setShowAttestation] = useState(false);
  const [attestChecked1, setAttestChecked1] = useState(false);
  const [attestChecked2, setAttestChecked2] = useState(false);
  const { isFounder } = useContext(FounderContext);

  const handleTranslate = async () => {
    if (!result?.soap) return;
    setIsTranslating(true);
    setTranslateError(null);
    setTranslatedSoap(null);

    const soap = result.soap;
    const riskFlags = result.riskFlags;
    const soapText = [
      `Chief Complaint: ${soap.cc || ''}`,
      `\nSubjective:\n${soap.subjective || ''}`,
      `\nAssessment:\n${soap.assessment || ''}`,
      `\nPlan:\n${soap.plan || ''}`,
      riskFlags?.length ? `\nRisk Flags: ${riskFlags.map(f => `[${f.severity}] ${f.category}: ${f.message}`).join('; ')}` : '',
    ].filter(Boolean).join('\n');

    try {
      const res = await fetch(`${API}/sarvam/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: soapText,
          source_language_code: 'en-IN',
          target_language_code: translateLang,
          mode: 'formal',
        }),
      });
      const data = await res.json();
      if (data.translated_text) {
        setTranslatedSoap(data.translated_text);
      } else {
        setTranslateError(data.error || 'Translation failed');
      }
    } catch (err) {
      setTranslateError('Network error. Please try again.');
    }
    setIsTranslating(false);
  };

  if (!result) return null;

  const isPatientView = viewMode === 'patient';

  // SESSION 2 — Physician output restructure. The physician surface is now a
  // dedicated 3-zone accordion layout (PhysicianOutput). The patient surface
  // below is intentionally left untouched.
  if (!isPatientView) {
    return <PhysicianOutput result={result} onEdit={onEdit} onViewPatient={onViewPatient} />;
  }

  const { encounterId, visitDate, soap, riskFlags, guardrails, auditLog, healthMartRecommendations, structuredData, clinicalSummary, motivationSupport, providerFollowup, spellingAmbiguities, lonelinessSupport, psychosocialOffer, patientInputVerbatim, abbreviationAudit, drugAudit, preventiveRecommendations, preventiveMedicineCard, differentials, resourceBundles, transcriptionWarnings } = result;

  return (
    <section className="max-w-4xl mx-auto px-6 py-8" data-testid="clinical-output">
      <div className="intake-card p-6 md:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6 pb-4 border-b border-slate-200">
          <div>
            <h3 className="text-xl font-semibold text-slate-900">
              {isPatientView ? 'My Health Records Summary' : 'Physician SOAP Notes'}
            </h3>
            {patientInputVerbatim?.patientName && (
              <div className="text-sm font-medium text-slate-700 mt-1" data-testid="result-patient-name">
                Patient: {patientInputVerbatim.patientName}
              </div>
            )}
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isPatientView ? 'bg-[#007BFF] text-white' : 'bg-[#28a745] text-white'}`}>
                {isPatientView ? 'Patient View' : 'Physician View'}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
              <span data-testid="result-encounter-id">{encounterId}</span>
              <span>|</span>
              <span>{visitDate?.slice(0, 19)}</span>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => downloadPDF(`${API}/encounter/${encounterId}/patient-pdf`)} data-testid="btn-patient-pdf">
                <Download className="w-3.5 h-3.5 mr-1" /> Patient PDF
              </Button>
              {!isPatientView && (
                <Button size="sm" variant="outline" onClick={() => setShowAttestation(true)} data-testid="btn-soap-pdf">
                  <Download className="w-3.5 h-3.5 mr-1" /> SOAP PDF
                </Button>
              )}
              {isFounder && onEdit && (
                <Button size="sm" onClick={() => onEdit(encounterId)} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="btn-edit-intake">
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit Intake
                </Button>
              )}
            </div>
            {/* Translate SOAP — physician-only (May 17, 2026): patient surface intentionally omits clinical translation */}
            {!isPatientView && (
              <div className="flex items-center gap-1.5" data-testid="translate-section">
                <Select value={translateLang} onValueChange={setTranslateLang}>
                  <SelectTrigger className="h-8 text-xs w-[130px] border-slate-300" data-testid="translate-lang-select">
                    <Globe className="w-3 h-3 mr-1 text-[#0369A1]" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {Object.entries(TRANSLATE_LANGS).map(([code, name]) => (
                      <SelectItem key={code} value={code}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  onClick={handleTranslate}
                  disabled={isTranslating}
                  className="h-8 text-xs bg-[#0369A1] hover:bg-[#025b8a] text-white"
                  data-testid="translate-btn"
                >
                  {isTranslating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Globe className="w-3 h-3 mr-1" />}
                  Translate
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Session 4 (Part G) — patient-friendly result card, above the existing
            patient output (urgency badge · plain-language result · Health Mart CTA). */}
        {isPatientView && <PatientResultCard result={result} />}

        {/* Physician View Selector (May 17, 2026) — dropdown lets physician choose
            their preferred view depth. Some want a 1-line summary; others want full
            SOAP; some want longitudinal past history. */}
        {!isPatientView && (
          <div className="mb-4 flex items-center gap-3" data-testid="physician-view-selector">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Physician view:</span>
            <Select value={physicianView} onValueChange={setPhysicianView}>
              <SelectTrigger className="h-8 text-sm w-[280px] border-slate-300" data-testid="physician-view-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full" data-testid="opt-full">Full SOAP (Default)</SelectItem>
                <SelectItem value="summary" data-testid="opt-summary">Physician Summary only</SelectItem>
                <SelectItem value="history" data-testid="opt-history">Past History + Clinical Notes</SelectItem>
                <SelectItem value="provenance" data-testid="opt-provenance">Differentials + Engine Provenance</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Draft Notice — wording differs for patient vs physician (May 17, 2026 glossary) */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-sm text-amber-800 flex items-start gap-2" data-testid="draft-notice">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          {isPatientView ? (
            <span>This is your patient summary — what you entered, your wellness guidance, and questions to bring to your doctor. Not a diagnosis. Your physician will review your visit.</span>
          ) : (
            <span>DRAFT — For physician review only. System-generated clinical decision-support content. Not a diagnosis or treatment plan.</span>
          )}
        </div>

        {/* ─── PHYSICIAN ASSESSMENT REQUIRED — TOP BANNER (Feb 2026) ────
            Made deliberately prominent. The deterministic engines flag
            what they can parse; anything they cannot recognize is surfaced
            for physician judgment rather than silently expanded or assumed.
            This covers: spelling ambiguities, poor-quality voice / image /
            multimodal input, abbreviations, novel presentations not in the
            sealed rule catalog, and non-English idioms. */}
        <div
          className="bg-rose-50 border-2 border-rose-300 rounded-xl p-4 mb-4 text-sm text-rose-900 flex items-start gap-3 shadow-sm"
          data-testid="physician-assessment-required-banner"
        >
          <AlertTriangle className="w-6 h-6 mt-0.5 flex-shrink-0 text-rose-600" />
          <div className="space-y-1">
            <div className="font-bold text-rose-900 text-base">
              Physician / Provider assessment required
            </div>
            <div className="text-rose-800">
              This SOAP reflects only what the <span className="font-semibold">{INSTRUMENT_LIBRARY_COUNT} deterministic clinical engines</span> could
              recognize from the provided input. The system <span className="font-semibold">does not silently guess</span>.
              Anything the AI could not match — ambiguous spellings, poor-quality voice or image input,
              unfamiliar abbreviations, novel presentations, non-English idioms, or multimodal gaps —
              is <span className="font-semibold">surfaced below for physician judgment</span> rather than
              assumed. Treat every output as a draft requiring clinical sign-off.
            </div>
            <div className="text-xs text-rose-700 italic">
              Not a diagnosis. Not a treatment plan. Not emergency triage. NO LLM in the clinical risk path.
            </div>
          </div>
        </div>

        {/* ─── CLINICAL HUMILITY ANNOTATION (Task 3) ─────────────────
            Secondary contextual note (kept for backward compat). */}
        <div
          className="bg-sky-50 border border-sky-200 rounded-xl p-3 mb-4 text-xs sm:text-sm text-sky-900 flex items-start gap-2"
          data-testid="clinical-humility-annotation"
        >
          <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0 text-sky-600" />
          <span>
            <span className="font-semibold">Deterministic engine output.</span>{' '}
            This SOAP reflects what the {INSTRUMENT_LIBRARY_COUNT} deterministic clinical engines could parse from the provided intake.
            Any clinical concern not flagged here may still require physician judgment.
            The system does not replace clinical reasoning.
            When uncertain, the system flags for physician review rather than assuming.
          </span>
        </div>

        {/* ─── VERBATIM PATIENT INPUT (Task 4) ────────────────────────
            Reproduces the patient/physician input EXACTLY as received.
            No clean-up, no paraphrase, no correction. The physician
            sees the raw input alongside the structured SOAP and judges
            the gap themselves. Founder's no-fabrication principle. */}
        {patientInputVerbatim && (
          <details
            className="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-4"
            data-testid="patient-input-verbatim"
            open
          >
            <summary className="cursor-pointer text-sm font-semibold text-slate-700 flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-500" />
              Patient Input (verbatim) — click to collapse
              {patientInputVerbatim.isVoice && (
                <span className="ml-auto text-[10px] uppercase tracking-wide text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Voice input</span>
              )}
            </summary>
            <div className="mt-3 space-y-2 text-xs sm:text-sm text-slate-700 font-mono leading-relaxed">
              {patientInputVerbatim.isVoice && (
                <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                  Voice transcription — please verify accuracy before clinical use.
                </p>
              )}
              {patientInputVerbatim.chiefComplaint && (
                <div><span className="text-slate-500">Chief Complaint:</span> {patientInputVerbatim.chiefComplaint}</div>
              )}
              {patientInputVerbatim.symptoms && (
                <div><span className="text-slate-500">Symptoms:</span> {patientInputVerbatim.symptoms}</div>
              )}
              {patientInputVerbatim.medications && (
                <div><span className="text-slate-500">Medications:</span> {patientInputVerbatim.medications}</div>
              )}
              {patientInputVerbatim.existingConditions && (
                <div><span className="text-slate-500">Active Conditions:</span> {patientInputVerbatim.existingConditions}</div>
              )}
              {patientInputVerbatim.historicalConditions && (
                <div data-testid="verbatim-historical">
                  <span className="text-slate-500">Past Medical History (resolved/inactive):</span>{' '}
                  {patientInputVerbatim.historicalConditions}
                  <span className="ml-1 inline-block text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">scoped out of engines</span>
                </div>
              )}
              {patientInputVerbatim.formerExposures && (
                <div data-testid="verbatim-former">
                  <span className="text-slate-500">Former Exposures:</span>{' '}
                  {patientInputVerbatim.formerExposures}
                  <span className="ml-1 inline-block text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">scoped out of engines</span>
                </div>
              )}
              {patientInputVerbatim.notes && (
                <div><span className="text-slate-500">Notes:</span> {patientInputVerbatim.notes}</div>
              )}
              <p className="text-[10px] text-slate-400 pt-1 border-t border-slate-200 mt-2">
                Reproduced exactly as received. No clean-up, no paraphrase, no spelling correction.
                Compare against the structured SOAP below to judge any gap.
              </p>
            </div>
          </details>
        )}

        {/* ─── ABBREVIATION RESOLVER AUDIT ─────────────────────────────
            Shows every abbreviation that was deterministically expanded
            AND every ambiguous abbreviation that was surfaced for
            physician review rather than silently expanded. */}
        {abbreviationAudit && ((abbreviationAudit.resolved || []).length > 0 || (abbreviationAudit.unresolved || []).length > 0) && (
          <details
            className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4"
            data-testid="abbreviation-audit"
          >
            <summary className="cursor-pointer text-sm font-semibold text-emerald-900 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              Abbreviations resolved deterministically ({(abbreviationAudit.resolved || []).length}) · Unresolved ({(abbreviationAudit.unresolved || []).length})
            </summary>
            <div className="mt-3 space-y-1 text-xs sm:text-sm text-emerald-900">
              {(abbreviationAudit.resolved || []).map((r, i) => (
                <div key={`r-${i}`} className="flex items-baseline gap-2">
                  <code className="px-1.5 py-0.5 bg-emerald-100 rounded text-emerald-900 font-semibold">{r.abbrev}</code>
                  <span className="text-emerald-700">→</span>
                  <span>{r.expansion}</span>
                  <span className="text-[10px] text-emerald-600 ml-auto">{r.confidence} · {r.field}</span>
                </div>
              ))}
              {(abbreviationAudit.unresolved || []).map((u, i) => (
                <div key={`u-${i}`} className="flex items-baseline gap-2 text-amber-800 bg-amber-50 px-2 py-1 rounded">
                  <code className="px-1.5 py-0.5 bg-amber-100 rounded font-semibold">{u.abbrev}</code>
                  <span>— unresolved · possible meanings: <span className="font-medium">{(u.options || []).join(', ')}</span> · please verify before relying on this SOAP</span>
                </div>
              ))}
            </div>
          </details>
        )}

        {/* ─── DRUG NORMALIZER AUDIT ───────────────────────────────────
            Brand ↔ generic ↔ class mapping for every drug mentioned in
            the chart. Deterministic, traceable to RxNorm-backed entries. */}
        {drugAudit && (drugAudit.drugs || []).length > 0 && (
          <details
            className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 mb-4"
            data-testid="drug-normalizer-audit"
          >
            <summary className="cursor-pointer text-sm font-semibold text-indigo-900 flex items-center gap-2">
              <FileText className="w-4 h-4 text-indigo-600" />
              Drugs normalized ({(drugAudit.drugs || []).length}) · Classes detected ({(drugAudit.classes || []).length})
            </summary>
            <div className="mt-3 space-y-1 text-xs sm:text-sm text-indigo-900">
              {(drugAudit.drugs || []).map((d, i) => (
                <div key={`d-${i}`} className="flex items-baseline gap-2">
                  <code className="px-1.5 py-0.5 bg-indigo-100 rounded font-semibold">{d.mention}</code>
                  <span className="text-indigo-700">→</span>
                  <span>{d.generic}</span>
                  <span className="text-[10px] text-indigo-600 ml-auto">{d.is_brand ? 'brand' : 'generic'} · {d['class']}</span>
                </div>
              ))}
              {(drugAudit.classes || []).length > 0 && (
                <div className="text-[11px] text-indigo-700 pt-2 border-t border-indigo-200 mt-2">
                  <span className="font-semibold">Distinct drug classes:</span> {(drugAudit.classes || []).join(' · ')}
                </div>
              )}
            </div>
          </details>
        )}

        {/* ─── SPELLING / TERM AMBIGUITY — physician decision required ───
            When the deterministic spell-checker or ambiguity detector
            cannot resolve a term with certainty, the term is surfaced
            here rather than silently expanded. */}
        {/* FIX 1+2 (2026-06-11) — Clinical vocabulary collision warning. Physician
            output ONLY. Surfaces when a fuzzy correction that would create a
            clinically-invalid pairing (social↔sacral, lives↔liver) was reverted
            to verbatim. Never shown on the patient surface. */}
        {!isPatientView && Array.isArray(transcriptionWarnings) && transcriptionWarnings.length > 0 && (
          <div
            className="bg-yellow-50 border border-yellow-300 rounded-xl p-3 mb-4 text-xs sm:text-sm text-yellow-900"
            data-testid="transcription-warning-block"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-yellow-700" />
              <div>
                <p className="font-semibold mb-1">Possible transcription error — please verify patient input.</p>
                <ul className="list-disc list-inside space-y-1">
                  {transcriptionWarnings.map((w, i) => (
                    <li key={i} data-testid={`transcription-warning-${i}`}>
                      {w.detail || w.message}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {Array.isArray(spellingAmbiguities) && spellingAmbiguities.length > 0 && (
          <div
            className="bg-yellow-50 border border-yellow-300 rounded-xl p-3 mb-4 text-xs sm:text-sm text-yellow-900"
            data-testid="spelling-ambiguity-block"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-yellow-700" />
              <div>
                <p className="font-semibold mb-1">Term(s) unresolved — please verify before relying on this SOAP:</p>
                <ul className="list-disc list-inside space-y-1">
                  {spellingAmbiguities.map((amb, i) => (
                    <li key={i}>
                      <code className="px-1 py-0.5 bg-yellow-100 rounded">{amb.original || amb.term || JSON.stringify(amb)}</code>
                      {amb.options && Array.isArray(amb.options) && (
                        <span className="text-slate-600"> — possible meanings: {amb.options.join(', ')}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Translated SOAP Output */}
        {translateError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-sm text-red-700" data-testid="translate-error">
            Translation error: {translateError}
          </div>
        )}
        {translatedSoap && (
          <div className="bg-sky-50 border-2 border-[#0369A1] rounded-xl p-4 mb-4" data-testid="translated-soap">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-sm text-[#0369A1] flex items-center gap-1.5">
                <Globe className="w-4 h-4" />
                SOAP Note — {TRANSLATE_LANGS[translateLang] || translateLang}
              </h4>
              <button onClick={() => setTranslatedSoap(null)} className="text-xs text-slate-400 hover:text-slate-600">Close</button>
            </div>
            <pre className="text-sm text-slate-800 whitespace-pre-wrap font-[Manrope] leading-relaxed">{translatedSoap}</pre>
            <p className="text-[10px] text-slate-400 mt-2">Linguistic Translation Peripheral: Spoken intake translated to English data-payload via rule-based dictionary parsing. No clinical inference applied during translation. (Sarvam AI · for patient communication — not a replacement for the English clinical record.)</p>
          </div>
        )}

        {/* Negation Audit Warning */}
        {soap?.negationAudit?.negation_errors_found && (
          <div className={`rounded-xl p-3 mb-4 text-sm flex items-start gap-2 ${soap.negationAudit.blocked ? 'bg-red-50 border-2 border-red-300 text-red-800' : 'bg-amber-50 border border-amber-300 text-amber-800'}`} data-testid="negation-warning">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-bold">{soap.negationAudit.blocked ? 'NEGATION ERROR — Review Required' : 'Negation Warning'}</p>
              {soap.negationAudit.errors?.map((e, i) => (
                <p key={i} className="text-xs mt-1">[{e.severity}] {e.patient_said} → but {e.soap_assertion}</p>
              ))}
            </div>
          </div>
        )}

        {/* Low Confidence Warning */}
        {soap?.structuredSoap?.validation?.confidence_floor_met === false && (
          <div className="bg-yellow-50 border border-yellow-300 rounded-xl p-3 mb-4 text-sm text-yellow-800 flex items-start gap-2" data-testid="low-confidence-warning">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Low confidence ({soap.structuredSoap.assessment.confidence_score}) — physician review required. Ask patient for additional information.</span>
          </div>
        )}

        <Tabs defaultValue={isPatientView ? "summary" : "soap"} className="w-full">
          <TabsList className="flex flex-wrap w-full gap-1 h-auto bg-slate-100 p-1 rounded-xl">
            {isPatientView ? (
              <>
                <TabsTrigger value="summary" className="text-xs rounded-lg">My Summary</TabsTrigger>
                <TabsTrigger value="support" className="text-xs rounded-lg">Guidance</TabsTrigger>
                <TabsTrigger value="followup" className="text-xs rounded-lg">Questions for Doctor</TabsTrigger>
              </>
            ) : (
              <>
                <TabsTrigger value="soap" className="text-xs rounded-lg" data-testid="tab-soap">SOAP Note</TabsTrigger>
                <TabsTrigger value="risk" className="text-xs rounded-lg" data-testid="tab-risk">Risk Flags ({riskFlags?.length || 0})</TabsTrigger>
                <TabsTrigger value="summary" className="text-xs rounded-lg" data-testid="tab-summary">Summary</TabsTrigger>
                <TabsTrigger value="guardrails" className="text-xs rounded-lg" data-testid="tab-guardrails">Guardrails</TabsTrigger>
                <TabsTrigger value="audit" className="text-xs rounded-lg" data-testid="tab-audit">Decision Timeline</TabsTrigger>
                <TabsTrigger value="structured" className="text-xs rounded-lg" data-testid="tab-structured">Structured Data</TabsTrigger>
                {healthMartRecommendations?.length > 0 && (
                  <TabsTrigger value="healthmart" className="text-xs rounded-lg" data-testid="tab-healthmart">Health Mart</TabsTrigger>
                )}
                <TabsTrigger value="support" className="text-xs rounded-lg" data-testid="tab-support">Support</TabsTrigger>
                <TabsTrigger value="followup" className="text-xs rounded-lg" data-testid="tab-followup">Next Visit</TabsTrigger>
                <TabsTrigger value="physician" className="text-xs rounded-lg" data-testid="tab-physician">Physician Summary</TabsTrigger>
              </>
            )}
          </TabsList>

          {/* SOAP Note Tab */}
          <TabsContent value="soap" className="mt-4" data-testid="soap-content">
            {/* ── Physician view dropdown wiring (May 17, 2026) ──────────────
                'full' (default) = original SOAP body
                'summary'        = condensed 6–10 line view
                'history'        = chronological past-history timeline
                'provenance'     = differentials + engine provenance grouped by rule_id
                Patient view always renders the full body. */}

            {!isPatientView && physicianView === 'summary' && (
              <div className="space-y-3" data-testid="view-physician-summary">
                <div className="bg-slate-50 border-2 border-[#0369A1]/30 rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-wide font-bold text-[#0369A1] mb-2">Condensed Physician Summary</div>
                  <p className="text-sm text-slate-800 mb-2"><strong>Chief complaint:</strong> {soap?.cc || '—'}</p>
                  {soap?.assessment && (
                    <p className="text-sm text-slate-800 mb-2"><strong>Assessment (1-liner):</strong> {(soap.assessment.split('\n').filter(Boolean)[0] || '').slice(0, 240)}</p>
                  )}
                  {riskFlags?.length > 0 && (
                    <div className="mb-2">
                      <p className="text-sm font-bold text-slate-800 mb-1">Top {Math.min(3, riskFlags.length)} flag(s):</p>
                      <ul className="text-sm text-slate-700 list-disc list-inside space-y-0.5">
                        {riskFlags.slice(0, 3).map((flag, i) => (
                          <li key={`sum-flag-${i}`} data-testid={`summary-flag-${i}`}>
                            <strong>[{flag.severity}]</strong> {flag.category} — {flag.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p className="text-xs italic text-slate-500 mt-2">Suggested — physician review required. Switch to <em>Full SOAP</em> for complete S/O/A/P body.</p>
                </div>
              </div>
            )}

            {!isPatientView && physicianView === 'history' && (
              <div className="space-y-3" data-testid="view-past-history">
                <div className="bg-slate-50 border-2 border-[#0369A1]/30 rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-wide font-bold text-[#0369A1] mb-2">Past History &amp; Clinical Notes</div>

                  {/* Current visit summary */}
                  <div className="mb-3 pb-3 border-b border-slate-200">
                    <p className="text-xs font-bold text-slate-600 uppercase mb-1">This visit</p>
                    <p className="text-sm text-slate-800"><strong>Chief complaint:</strong> {soap?.cc || '—'}</p>
                    {soap?.subjective && <p className="text-sm text-slate-700 mt-1"><strong>Subjective:</strong> {soap.subjective.slice(0, 320)}</p>}
                  </div>

                  {/* Existing conditions / medications from intake */}
                  {(result?.patientInputVerbatim?.existingConditions || result?.existingConditions) && (
                    <div className="mb-3">
                      <p className="text-xs font-bold text-slate-600 uppercase mb-1">Existing conditions (patient-reported)</p>
                      <p className="text-sm text-slate-700">{result?.patientInputVerbatim?.existingConditions || result?.existingConditions || '—'}</p>
                    </div>
                  )}
                  {(result?.patientInputVerbatim?.medications || result?.medications) && (
                    <div className="mb-3">
                      <p className="text-xs font-bold text-slate-600 uppercase mb-1">Current medications</p>
                      <p className="text-sm text-slate-700">{result?.patientInputVerbatim?.medications || result?.medications || '—'}</p>
                    </div>
                  )}

                  {/* Surgical history — structured rows + free text */}
                  {(result?.surgicalHistoryRows?.length > 0 || result?.surgicalHistoryFreeText) && (
                    <div className="mb-3">
                      <p className="text-xs font-bold text-slate-600 uppercase mb-1">Past surgical history (chronological)</p>
                      {result?.surgicalHistoryRows?.length > 0 && (
                        <ul className="text-sm text-slate-700 list-disc list-inside space-y-0.5">
                          {[...result.surgicalHistoryRows]
                            .sort((a, b) => (parseInt(b.year) || 0) - (parseInt(a.year) || 0))
                            .map((row, i) => row.procedure && (
                              <li key={`sx-hist-${i}`} data-testid={`history-sx-${i}`}>
                                <strong>{row.year || '?'}</strong> — {row.procedure}
                                {row.facility && <span className="text-slate-500"> · {row.facility}</span>}
                                {row.complication && <span className="text-red-600 font-semibold"> · Complication: {row.complication}</span>}
                              </li>
                            ))}
                        </ul>
                      )}
                      {result?.surgicalHistoryFreeText && (
                        <p className="text-sm text-slate-700 mt-1 italic">&ldquo;{result.surgicalHistoryFreeText}&rdquo;</p>
                      )}
                    </div>
                  )}

                  <p className="text-xs italic text-slate-500 mt-2 border-l-2 border-amber-400 pl-2">
                    Multi-visit longitudinal timeline (prior encounter aggregation) will populate from longitudinal-memory step in production. Switch to <em>Full SOAP</em> for current-visit S/O/A/P body.
                  </p>
                </div>
              </div>
            )}

            {!isPatientView && physicianView === 'provenance' && (
              <div className="space-y-3" data-testid="view-provenance">
                <div className="bg-slate-50 border-2 border-[#0369A1]/30 rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-wide font-bold text-[#0369A1] mb-2">Differentials &amp; Engine Provenance</div>
                  <p className="text-xs text-slate-600 mb-3">Each flag traced to its rule_id, source guideline, and the patient-text trigger that fired it. Cures Act-aligned audit trail.</p>

                  {riskFlags?.length > 0 ? (
                    (() => {
                      // Group flags by engine / rule_id prefix
                      const groups = {};
                      riskFlags.forEach((f) => {
                        const key = (f.rule_id || f.ruleId || f.engine || f.engine_id || 'unknown_engine').split('_').slice(0, 2).join('_') || 'unknown_engine';
                        if (!groups[key]) groups[key] = [];
                        groups[key].push(f);
                      });
                      return Object.entries(groups).map(([engineKey, flags], gi) => (
                        <div key={`prov-eng-${gi}`} className="mb-3 pb-3 border-b border-slate-200 last:border-b-0" data-testid={`provenance-engine-${engineKey}`}>
                          <p className="text-[11px] font-mono font-bold text-slate-600 mb-1">Engine: {engineKey}</p>
                          {flags.map((flag, fi) => (
                            <div key={`prov-flag-${gi}-${fi}`} className="ml-2 mb-2">
                              <p className="text-sm text-slate-800"><strong>[{flag.severity}]</strong> {flag.category} — {flag.message}</p>
                              <div className="text-[11px] text-slate-500 ml-2 mt-0.5 leading-snug">
                                <div><span className="font-mono">rule_id:</span> {flag.rule_id || flag.ruleId || '(unknown)'}</div>
                                <div><span className="font-mono">source_guideline:</span> {flag.source_guideline || flag.sourceGuideline || 'guideline_pending'}</div>
                                {flag.triggers?.length > 0 && (
                                  <div><span className="font-mono">trigger_tokens:</span> {flag.triggers.join(' | ')}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ));
                    })()
                  ) : (
                    <p className="text-sm text-slate-500 italic">No flags fired on this encounter. Nothing to audit.</p>
                  )}
                </div>
              </div>
            )}

            {(isPatientView || physicianView === 'full') && (
            <div className="space-y-4">
              <div>
                <div className="soap-section-title">CC (Chief Complaint)</div>
                <p className="text-sm text-slate-800">{soap?.cc}</p>
              </div>
              <div>
                <div className="soap-section-title">Subjective</div>
                <pre className="text-sm text-slate-800 whitespace-pre-wrap font-[Manrope]">{soap?.subjective}</pre>
              </div>
              <div>
                <div className="soap-section-title">Objective</div>
                <p className="text-sm text-slate-600 italic">{soap?.objective}</p>
              </div>
              <div>
                <div className="soap-section-title">Assessment</div>
                <pre className="text-sm text-slate-800 whitespace-pre-wrap font-[Manrope]">{soap?.assessment}</pre>
              </div>

              {/* OPQRST capture completeness — structured-intake signal (deterministic, no LLM) */}
              <OpqrstChip cc={result.chiefComplaintStructured} />

              {/* Evidence Map — validated instruments that fired (deterministic, no LLM) */}
              <EvidenceMapBadges riskFlags={riskFlags} evidenceMap={result.evidenceMap} />

              {/* Risk Flags inline in SOAP */}
              {riskFlags?.length > 0 && (
                <div>
                  <div className="soap-section-title flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500" /> Clinical Risk Flags
                  </div>
                  {riskFlags.map((flag, i) => <RiskFlagCard key={i} flag={flag} />)}
                </div>
              )}

              {/* Engine 25 — Loneliness/Social Isolation support card (deterministic, no LLM) */}
              {lonelinessSupport?.card && (
                <div
                  className={
                    'mt-4 rounded-xl p-4 border-2 ' +
                    (lonelinessSupport.cardType === 'crisis'
                      ? 'bg-red-50 border-red-400'
                      : lonelinessSupport.cardType === 'bereavement'
                      ? 'bg-amber-50 border-amber-300'
                      : 'bg-indigo-50 border-indigo-300')
                  }
                  data-testid="loneliness-support-card"
                >
                  <h4
                    className={
                      'font-bold text-sm mb-2 ' +
                      (lonelinessSupport.cardType === 'crisis'
                        ? 'text-red-800'
                        : lonelinessSupport.cardType === 'bereavement'
                        ? 'text-amber-900'
                        : 'text-indigo-900')
                    }
                    data-testid="loneliness-support-title"
                  >
                    {lonelinessSupport.card.title}
                  </h4>
                  <p className="text-sm text-slate-800 leading-relaxed" data-testid="loneliness-support-body">
                    {lonelinessSupport.card.body}
                  </p>

                  {lonelinessSupport.crisisHelplines?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-300/50">
                      <p className="text-xs font-semibold text-slate-700 mb-2">Support helplines:</p>
                      <ul className="space-y-1.5">
                        {lonelinessSupport.crisisHelplines.map((h, i) => (
                          <li key={i} className="text-xs text-slate-800" data-testid={`loneliness-helpline-${i}`}>
                            <span className="font-medium">{h.name}:</span>{' '}
                            <span className="font-mono">{h.number}</span>
                            {h.hours && <span className="text-slate-500"> · {h.hours}</span>}
                            {h.language && <span className="text-slate-500"> · {h.language}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {lonelinessSupport.supportResources?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-300/50">
                      <p className="text-xs font-semibold text-slate-700 mb-2">Community resources:</p>
                      <ul className="space-y-1 text-xs text-slate-700">
                        {lonelinessSupport.supportResources.map((r, i) => (
                          <li key={i} data-testid={`loneliness-resource-${i}`}>
                            <span className="font-medium">{r.name}</span>
                            {r.audience && <span className="text-slate-500"> — {r.audience}</span>}
                            {r.url && <span className="text-slate-500"> · {r.url}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {lonelinessSupport.card.attribution && (
                    <p className="text-[10px] italic text-slate-500 mt-3" data-testid="loneliness-support-attribution">
                      {lonelinessSupport.card.attribution}
                    </p>
                  )}
                </div>
              )}

              {/* Engine 26 — Structured Psychosocial Response Layer (deterministic, ≤3 cards, NO LLM) */}
              <PsychosocialSession encounterId={encounterId} offer={psychosocialOffer} />

              {/* Engine 50 — Patient & Caregiver Resources (developmental disability — non-clinical informational) */}
              <PatientCaregiverResources resourceBundles={resourceBundles} />

              {/* Differential Diagnosis — Dr. Devarajan May 2026 (deterministic, NO LLM) */}
              {differentials && differentials.length > 0 && (
                <div
                  className="mt-4 bg-slate-50 border-2 border-slate-300 rounded-xl p-4"
                  data-testid="differential-diagnosis-card"
                >
                  <h4 className="font-bold text-slate-900 text-sm mb-1 flex items-center gap-2">
                    Differential Diagnosis
                  </h4>
                  <p className="text-[11px] italic text-slate-600 mb-3">
                    What else could this be — and the specific test to rule each one out. Deterministic mapping from risk flags. NO LLM. Physician confirms.
                  </p>
                  <div className="space-y-2">
                    {differentials.map((dx, i) => {
                      const tint = dx.urgencyColor === 'red'
                        ? { bg: 'bg-red-50', border: 'border-red-300', badge: 'bg-red-600 text-white', label: 'CRITICAL' }
                        : dx.urgencyColor === 'orange'
                        ? { bg: 'bg-orange-50', border: 'border-orange-300', badge: 'bg-orange-500 text-white', label: 'HIGH' }
                        : { bg: 'bg-amber-50', border: 'border-amber-300', badge: 'bg-amber-500 text-white', label: 'MODERATE' };
                      return (
                        <div
                          key={`${dx.condition}-${i}`}
                          className={`${tint.bg} ${tint.border} border rounded-lg p-3`}
                          data-testid={`differential-${i}`}
                        >
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span
                              className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-bold ${tint.badge}`}
                              data-testid={`differential-urgency-${i}`}
                            >
                              {dx.urgency || tint.label}
                            </span>
                            <span
                              className="font-semibold text-slate-900 text-sm"
                              data-testid={`differential-condition-${i}`}
                            >
                              {dx.condition}
                            </span>
                            {dx.parentFlagCategory && (
                              <span className="text-[10px] text-slate-400 ml-auto">
                                from {dx.parentFlagCategory}
                              </span>
                            )}
                          </div>
                          <div
                            className="text-[12px] text-slate-700 mt-1"
                            data-testid={`differential-test-${i}`}
                          >
                            <span className="font-semibold text-slate-800">Test to rule out:</span> {dx.testToRuleOut}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <div className="soap-section-title">Plan</div>
                <pre className="text-sm text-slate-800 whitespace-pre-wrap font-[Manrope]">{soap?.plan}</pre>
              </div>

              {/* USPSTF Preventive Recommendations — Grade A + B (deterministic, NO LLM) */}
              {preventiveRecommendations && preventiveRecommendations.length > 0 && (
                <div className="mt-4 bg-emerald-50 border-2 border-emerald-300 rounded-xl p-4" data-testid="uspstf-recommendations">
                  <h4 className="font-bold text-emerald-900 text-sm mb-1 flex items-center gap-2">
                    Preventive Recommendations — USPSTF Grade A / B
                  </h4>
                  <p className="text-[11px] italic text-emerald-700 mb-3">
                    Deterministic match on patient age, sex, and risk factors. U.S. Preventive Services Task Force, public domain.
                  </p>
                  <div className="space-y-2">
                    {preventiveRecommendations.map((rec, i) => (
                      <div
                        key={rec.rec_id || i}
                        className="bg-white border border-emerald-200 rounded-lg p-3 text-sm"
                        data-testid={`uspstf-rec-${rec.rec_id || i}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={
                            'inline-block text-[10px] px-1.5 py-0.5 rounded font-bold ' +
                            (rec.grade === 'A'
                              ? 'bg-emerald-600 text-white'
                              : 'bg-emerald-400 text-white')
                          }>
                            Grade {rec.grade}
                          </span>
                          <span className="font-semibold text-slate-900">{rec.topic}</span>
                          {rec.recency_status && (
                            <span
                              data-testid={`uspstf-recency-${rec.rec_id || i}`}
                              className={
                                'inline-block text-[10px] px-1.5 py-0.5 rounded font-bold ' +
                                (rec.recency_status === 'overdue'
                                  ? 'bg-red-600 text-white'
                                  : rec.recency_status === 'up_to_date'
                                    ? 'bg-slate-200 text-slate-700'
                                    : 'bg-amber-400 text-amber-950')
                              }
                            >
                              {rec.recency_status === 'overdue'
                                ? 'OVERDUE'
                                : rec.recency_status === 'up_to_date'
                                  ? 'UP TO DATE'
                                  : 'NO PRIOR ON RECORD'}
                            </span>
                          )}
                          <span className="text-[10px] text-slate-400 ml-auto">{rec.rec_id}</span>
                        </div>
                        <div className="text-slate-700">{rec.plan_text}</div>
                        {rec.recency_note && (
                          <div
                            className={
                              'text-[11px] mt-1 font-medium ' +
                              (rec.recency_status === 'overdue' ? 'text-red-700' : 'text-slate-500')
                            }
                            data-testid={`uspstf-recency-note-${rec.rec_id || i}`}
                          >
                            {rec.recency_note}
                          </div>
                        )}
                        <div className="text-[11px] text-slate-500 italic mt-1">Eligibility: {rec.population}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* PrevenCard — Preventive Medicine, Evidence-Based (Engine 27) */}
              {/* 4 WHO levels — primordial / primary / secondary / tertiary. NO LLM. */}
              {preventiveMedicineCard && preventiveMedicineCard.matches && preventiveMedicineCard.matches.length > 0 && (
                <div className="mt-4 bg-gradient-to-br from-indigo-50 to-sky-50 border-2 border-indigo-300 rounded-xl p-4" data-testid="prevencard-card">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-bold text-indigo-900 text-sm flex items-center gap-2">
                        Preventive Medicine — Evidence-Based
                      </h4>
                      <p className="text-[11px] italic text-indigo-700 mt-0.5">
                        WHO 4-level prevention framework · deterministic match on age, sex, region, season, PMH · {preventiveMedicineCard.matches.length} recommendation{preventiveMedicineCard.matches.length === 1 ? '' : 's'}
                      </p>
                    </div>
                    {preventiveMedicineCard.total_avoided_cost_usd > 0 && (
                      <div className="text-right shrink-0">
                        <div className="text-[10px] uppercase tracking-wide text-indigo-600">Projected ER / hospitalization cost avoided</div>
                        <div className="text-xl font-bold text-indigo-900" data-testid="prevencard-avoided-cost">
                          ${preventiveMedicineCard.total_avoided_cost_usd.toLocaleString()}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    {preventiveMedicineCard.matches.map((m, i) => {
                      const levelStyles = {
                        primordial: { chip: 'bg-slate-600 text-white', label: 'Primordial' },
                        primary:    { chip: 'bg-emerald-600 text-white', label: 'Primary' },
                        secondary:  { chip: 'bg-amber-500 text-white', label: 'Secondary' },
                        tertiary:   { chip: 'bg-orange-600 text-white', label: 'Tertiary' },
                      };
                      const lvl = levelStyles[m.prevention_level] || levelStyles.primary;
                      return (
                        <div
                          key={m.rule_id || i}
                          className="bg-white border border-indigo-200 rounded-lg p-3 text-sm"
                          data-testid={`prevencard-rule-${m.rule_id}`}
                        >
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className={`inline-block text-[10px] px-2 py-0.5 rounded font-bold ${lvl.chip}`}>
                              {lvl.label}
                            </span>
                            <span className="font-semibold text-slate-900">{m.rule_name}</span>
                            {m.evidence_grade && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 border border-indigo-200">
                                Grade {m.evidence_grade}
                              </span>
                            )}
                            {m.icd10_z_code && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-900 text-white font-mono">
                                {m.icd10_z_code}
                              </span>
                            )}
                            <span className="text-[10px] text-slate-400 ml-auto">{m.rule_id}</span>
                          </div>
                          <div className="text-slate-700">{m.recommendation_text}</div>
                          <div className="text-[11px] text-slate-500 italic mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
                            <span>Source: {m.source_code}</span>
                            {m.evidence_url && (
                              <a
                                href={m.evidence_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-600 hover:text-indigo-800 underline"
                                data-testid={`prevencard-evidence-${m.rule_id}`}
                              >
                                Evidence link
                              </a>
                            )}
                            {m.avoided_cost_usd > 0 && (
                              <span className="text-emerald-700">Avoided cost: ${m.avoided_cost_usd.toLocaleString()}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-[10px] text-indigo-600 italic mt-2">
                    Draft preventive guidance — physician review required. Not a substitute for emergency triage.
                  </div>
                </div>
              )}

              {/* Engine 28 — Nudge Orchestrator inbox (fail-proof, sealed-scope) */}
              <NudgeInbox encounterId={encounterId} />

              {/* Ambiguous Spelling Alert — Physician Review */}
              {spellingAmbiguities?.length > 0 && (
                <div className="mt-4 bg-orange-50 border-2 border-orange-300 rounded-xl p-4" data-testid="spelling-ambiguity-box">
                  <h4 className="font-bold text-orange-800 text-sm mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    SPELLING AMBIGUITY — Physician Verification Required
                  </h4>
                  <p className="text-xs text-orange-700 mb-2">The following words may be misspelled. The system flags these for physician review rather than auto-correcting medical terms.</p>
                  <div className="space-y-1.5">
                    {spellingAmbiguities.map((amb, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm bg-white/60 rounded-lg p-2">
                        <span className="text-orange-600 font-bold text-xs mt-0.5">?</span>
                        <div>
                          <span className="font-medium text-orange-900">&ldquo;{amb.found}&rdquo;</span>
                          <span className="text-orange-700"> — did the patient mean </span>
                          <span className="font-medium text-orange-900">&ldquo;{amb.intended}&rdquo;</span>
                          <span className="text-orange-600">?</span>
                          {amb.context && <span className="text-xs text-orange-500 ml-1">(found in: &ldquo;{amb.context}&rdquo;)</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-orange-500 mt-2">Flagging ambiguous words for physician review is clinically safer than auto-correcting medical terms.</p>
                </div>
              )}

              {/* Provider Box — CRITICAL flags + SKIN VARIABLE */}
              {riskFlags?.some(f => f.severity === 'CRITICAL' || f.skinVariable) && (
                <div className="mt-4 border-4 border-double border-red-600 rounded-xl p-4 bg-red-50" data-testid="provider-box">
                  <h4 className="font-bold text-red-800 text-sm mb-2 flex items-center gap-2">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                    PROVIDER ALERT — HIGH PRIORITY
                  </h4>
                  {riskFlags.filter(f => f.severity === 'CRITICAL').map((f, i) => (
                    <p key={i} className="text-sm text-red-900 font-medium mb-1">[{f.severity}] {f.category}: {f.message}</p>
                  ))}
                  {riskFlags.filter(f => f.skinVariable).map((f, i) => (
                    <p key={`skin-${i}`} className="text-sm text-amber-800 mt-1">[SKIN VARIABLE] {f.category}: Symptoms may present differently on darker skin — enhanced examination recommended.</p>
                  ))}
                </div>
              )}

              {/* Patient Bridge — Questions to Ask Your Doctor */}
              {soap?.patientBridge?.length > 0 && (
                <div className="mt-4 bg-blue-50 border-2 border-[#2563EB] rounded-xl p-4" data-testid="patient-bridge">
                  <h4 className="font-bold text-[#2563EB] text-sm mb-2">Questions to Ask Your Doctor</h4>
                  <p className="text-xs text-slate-500 mb-2">Based on your intake and health history, consider asking:</p>
                  <ol className="list-decimal pl-5 space-y-1.5">
                    {soap.patientBridge.map((q, i) => (
                      <li key={i} className="text-sm text-slate-800">{q}</li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Evidence Integrity Report */}
              {riskFlags?.some(f => f.category?.includes('EVIDENCE INTEGRITY')) && (
                <div className="mt-4 bg-amber-50 border-2 border-amber-400 rounded-xl p-4" data-testid="evidence-integrity-report">
                  <h4 className="font-bold text-amber-900 text-sm mb-2 flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5" />
                    EVIDENCE INTEGRITY REPORT
                  </h4>
                  {riskFlags.filter(f => f.category?.includes('EVIDENCE INTEGRITY')).map((f, i) => (
                    <div key={i} className="mb-3 last:mb-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`text-[10px] ${f.severity === 'CRITICAL' ? 'bg-red-600' : f.severity === 'HIGH' ? 'bg-orange-500' : 'bg-amber-500'} text-white`}>
                          {f.integrityStatus || f.severity}
                        </Badge>
                      </div>
                      <p className="text-sm text-amber-900">{f.message}</p>
                      {f.concerns?.length > 0 && (
                        <ul className="mt-1 text-xs text-amber-800 list-disc pl-5">
                          {f.concerns.map((c, j) => <li key={j}>{c}</li>)}
                        </ul>
                      )}
                      <p className="text-xs text-amber-700 mt-1 font-medium">
                        Physician Action: Recommend source verification and assessment for clinical masking.
                        {' '}Curated alternatives available through <span className="text-[#EF4444] font-bold">P.AI Health Mart</span>.
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}
          </TabsContent>
          <TabsContent value="risk" className="mt-4" data-testid="risk-content">
            {riskFlags?.length > 0 ? (
              <div className="space-y-2">
                {riskFlags.map((flag, i) => <RiskFlagCard key={i} flag={flag} />)}
              </div>
            ) : (
              <div className="text-center py-8 text-sm text-slate-500">
                <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                No significant risk flags identified for this encounter.
              </div>
            )}
          </TabsContent>

          {/* Clinical Summary Tab */}
          <TabsContent value="summary" className="mt-4" data-testid="summary-content">
            {/* Evidence Map — validated instruments that fired (deterministic, no LLM) */}
            <div className="mb-4">
              <EvidenceMapBadges riskFlags={riskFlags} evidenceMap={result.evidenceMap} />
            </div>
            <pre className="text-sm text-slate-800 whitespace-pre-wrap font-[Manrope] bg-slate-50 p-4 rounded-xl">{clinicalSummary}</pre>
          </TabsContent>

          {/* Guardrails Tab */}
          <TabsContent value="guardrails" className="mt-4" data-testid="guardrails-content">
            <div className={`p-4 rounded-xl mb-4 ${guardrails?.passed ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                {guardrails?.passed ? <ShieldCheck className="w-5 h-5 text-emerald-600" /> : <AlertTriangle className="w-5 h-5 text-amber-600" />}
                <span className="font-semibold text-sm">{guardrails?.status}</span>
              </div>
            </div>
            <div className="space-y-2">
              {guardrails?.checks?.map((check, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 text-sm">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span className="text-slate-700">{check}</span>
                </div>
              ))}
              {guardrails?.issues?.map((issue, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-red-50 text-sm">
                  <XCircle className="w-4 h-4 text-red-500" />
                  <span className="text-red-700">{issue}</span>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit" className="mt-4" data-testid="audit-content">
            <h4 className="font-bold text-sm text-slate-900 mb-3">Clinical Decision Timeline</h4>
            <ScrollArea className="max-h-64">
              <div className="relative pl-6 border-l-2 border-[#2563EB]/30 space-y-3">
                {auditLog?.map((entry, i) => (
                  <div key={i} className="relative" data-testid={`audit-entry-${i}`}>
                    <div className="absolute -left-[25px] w-3 h-3 rounded-full bg-[#2563EB] border-2 border-white"></div>
                    <div className="p-2.5 rounded-lg bg-slate-50 text-xs">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-slate-800">{entry.action}</span>
                        <Badge variant="outline" className="text-[10px]">{entry.status}</Badge>
                      </div>
                      <p className="text-slate-500">{entry.details}</p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Structured Data Tab */}
          <TabsContent value="structured" className="mt-4" data-testid="structured-content">
            <pre className="text-xs text-slate-700 bg-slate-50 p-4 rounded-xl overflow-auto font-mono max-h-96">{JSON.stringify(structuredData, null, 2)}</pre>
          </TabsContent>

          {/* Health Mart Tab */}
          {healthMartRecommendations?.length > 0 && (
            <TabsContent value="healthmart" className="mt-4" data-testid="healthmart-content">
              <div className="space-y-3">
                {healthMartRecommendations.map((rec, i) => (
                  <div key={i} className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <ShoppingBag className="w-4 h-4 text-[#0369A1]" />
                      <Badge variant="outline" className="text-[10px]">{rec.category}</Badge>
                    </div>
                    <h4 className="font-semibold text-sm text-slate-900">{rec.title}</h4>
                    <p className="text-xs text-slate-600 mt-1">{rec.description}</p>
                  </div>
                ))}
              </div>
            </TabsContent>
          )}

          {/* Supportive Guidance Tab */}
          <TabsContent value="support" className="mt-4" data-testid="support-content">
            {motivationSupport && (
              <div className={`p-5 rounded-xl ${motivationSupport.blocked ? 'bg-red-50 border-2 border-red-300' : 'bg-emerald-50 border border-emerald-200'}`}>
                <h4 className={`font-bold text-base mb-2 ${motivationSupport.blocked ? 'text-red-800' : 'text-emerald-800'}`}>
                  {motivationSupport.heading}
                </h4>
                <p className={`text-sm mb-3 ${motivationSupport.blocked ? 'text-red-700' : 'text-slate-700'}`}>
                  {motivationSupport.message}
                </p>
                {motivationSupport.blocked && motivationSupport.actions && (
                  <ul className="space-y-1.5 mb-3">
                    {motivationSupport.actions.map((a, i) => (
                      <li key={i} className="text-sm text-red-800 font-medium flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />{a}
                      </li>
                    ))}
                  </ul>
                )}
                {motivationSupport.nextStep && (
                  <div className="bg-white/60 rounded-lg p-3 mb-2">
                    <p className="text-xs font-semibold text-emerald-700 mb-1">Your Next Step:</p>
                    <p className="text-sm text-slate-800">{motivationSupport.nextStep}</p>
                  </div>
                )}
                <p className="text-[10px] text-slate-400 mt-2">{motivationSupport.disclaimer}</p>
              </div>
            )}
          </TabsContent>

          {/* Next Visit Follow-Up Tab */}
          <TabsContent value="followup" className="mt-4" data-testid="followup-content">
            {providerFollowup?.length > 0 && (
              <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl">
                <h4 className="font-bold text-sm text-[#2563EB] mb-3">Next Visit Follow-Up Prompts</h4>
                <p className="text-xs text-slate-500 mb-3">Provider-facing: suggested questions for continuity of care</p>
                <ol className="list-decimal pl-5 space-y-2">
                  {providerFollowup.map((q, i) => (
                    <li key={i} className="text-sm text-slate-800">{q}</li>
                  ))}
                </ol>
                <p className="text-[10px] text-slate-400 mt-3">Behavioral context only — not diagnostic. Draft for clinician review.</p>
              </div>
            )}
          </TabsContent>

          {/* Physician Summary Tab */}
          <TabsContent value="physician" className="mt-4" data-testid="physician-content">
            {soap?.physicianSummary && (
              <div className="p-5 bg-white border-2 border-[#2563EB] rounded-xl">
                <h4 className="font-bold text-base text-[#2563EB] mb-3">Patient Health Summary — Physician Review</h4>
                <pre className="text-sm text-slate-800 whitespace-pre-wrap font-[Manrope] leading-relaxed">{soap.physicianSummary}</pre>
              </div>
            )}
          </TabsContent>


        </Tabs>

        {/* Patient footer disclaimer (May 17, 2026) — patient surface only */}
        {isPatientView && (
          <div className="mt-8 pt-4 border-t border-slate-200 text-xs text-slate-500 leading-relaxed" data-testid="patient-footer-disclaimer">
            P.AI Global Health (Patient AI) is a clinical documentation tool. It is not a doctor, does not diagnose,
            and does not provide medical advice. Your physician will review your visit and follow up with you.
            If you are experiencing a medical emergency, call emergency services immediately.
          </div>
        )}

        {/* Physician Attestation Modal (May 17, 2026) — must be confirmed before SOAP export */}
        {showAttestation && !isPatientView && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="attestation-modal">
            <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-3">Active Assertive Attestation</h3>
              <p className="text-sm text-slate-600 mb-4">
                Before exporting this SOAP note, please confirm both statements. This attestation is logged
                with the encounter record (Cures Act / SaMD alignment).
              </p>
              <label className="flex items-start gap-3 mb-3 text-sm text-slate-800 cursor-pointer">
                <input type="checkbox" className="mt-1" checked={attestChecked1} onChange={e => setAttestChecked1(e.target.checked)} data-testid="attest-check-1" />
                <span>I explicitly certify that I have read the generated clinical note in its entirety and have verified that all differentials and risk flags match my independent, final medical judgment.</span>
              </label>
              <label className="flex items-start gap-3 mb-5 text-sm text-slate-800 cursor-pointer">
                <input type="checkbox" className="mt-1" checked={attestChecked2} onChange={e => setAttestChecked2(e.target.checked)} data-testid="attest-check-2" />
                <span>I understand P.AI Global Health acts strictly as an administrative, rule-based decision support tool, and I am the sole diagnostic decision-maker for clinical action regarding this patient.</span>
              </label>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => { setShowAttestation(false); setAttestChecked1(false); setAttestChecked2(false); }} data-testid="attest-cancel">
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={!attestChecked1 || !attestChecked2}
                  className="bg-[#0369A1] hover:bg-[#025b8a] text-white"
                  onClick={() => {
                    setShowAttestation(false);
                    setAttestChecked1(false);
                    setAttestChecked2(false);
                    downloadPDF(`${API}/encounter/${encounterId}/soap-pdf`);
                  }}
                  data-testid="attest-confirm"
                >
                  Confirm & Download SOAP PDF
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
